import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { supabase } from '@/lib/supabase';
import { brandService } from '@/lib/brandService';
import { useBrand } from '@/context/BrandContext';
import { sessionCache } from '@/lib/sessionCache';
import { safeLocalStorage } from '@/lib/storage-utils';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import {
  Globe,
  Layers,
  Briefcase,
  ArrowRight,
  CheckSquare,
  Circle,
  CheckCircle2,
  Clock,
  Loader2,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Crown,
  FileText,
  Calendar as CalendarIcon,
} from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';

interface HomeProps {
  isGlobalDarkMode: boolean;
}

// ─── Data Types ──────────────────────────────────────────────────────────────

interface RecentTask {
  id: string;
  title: string;
  priority: string;
  due_date: string | null;
  status: string;
  board_name?: string;
}

interface RecentBrand {
  id: string;
  name: string;
  logo_url: string | null;
  colors: { primary: string; secondary: string; accent: string };
  status: string;
  industry: string;
}

interface RecentSite {
  id: string;
  name: string;
  url: string;
  uptime_status: string;
  thumbnail_url: string | null;
  wp_version: string;
}

interface RecentProject {
  id: string;
  name: string;
  status: string;
  progress: number;
  created_at: string;
}

interface RecentProposal {
  id: string;
  title: string;
  client_name?: string;
  company_name?: string;
  status: string;
  valid_until?: string | null;
  created_at?: string;
}

interface RecentBooking {
  id: string;
  title: string;
  client_name: string;
  client_email?: string;
  date: string;
  time: string;
  duration?: string;
  status: 'confirmed' | 'upcoming' | 'completed' | 'cancelled';
  service_type?: string;
}

const sampleProposals: RecentProposal[] = [
  {
    id: 'sample-1',
    title: 'Website Redesign & SEO Retainer',
    client_name: 'Acme Health Co.',
    company_name: 'Acme Health',
    status: 'pending',
    valid_until: new Date(Date.now() + 7 * 86400000).toISOString(),
    created_at: new Date(Date.now() - 2 * 86400000).toISOString(),
  },
  {
    id: 'sample-2',
    title: 'Brand Identity & Design System',
    client_name: 'Nexora Tech',
    company_name: 'Nexora Inc',
    status: 'approved',
    valid_until: new Date(Date.now() + 14 * 86400000).toISOString(),
    created_at: new Date(Date.now() - 5 * 86400000).toISOString(),
  },
  {
    id: 'sample-3',
    title: 'Custom Web Application Development',
    client_name: 'CloudScale Labs',
    company_name: 'CloudScale',
    status: 'draft',
    valid_until: new Date(Date.now() + 10 * 86400000).toISOString(),
    created_at: new Date(Date.now() - 1 * 86400000).toISOString(),
  },
];

const sampleBookings: RecentBooking[] = [
  {
    id: 'booking-1',
    title: 'Discovery & Scope Alignment',
    client_name: 'Sarah Jenkins',
    client_email: 'sarah@acme.io',
    date: 'Tomorrow',
    time: '10:00 AM',
    duration: '45m',
    status: 'confirmed',
    service_type: 'Video Call',
  },
  {
    id: 'booking-2',
    title: 'Brand Strategy Kickoff',
    client_name: 'Marcus Vance',
    client_email: 'marcus@nexora.co',
    date: 'Sep 8',
    time: '2:30 PM',
    duration: '60m',
    status: 'confirmed',
    service_type: 'Strategy Call',
  },
  {
    id: 'booking-3',
    title: 'Website Review & Feedback',
    client_name: 'Emma Watson',
    client_email: 'emma@pulse.studio',
    date: 'Sep 10',
    time: '11:00 AM',
    duration: '30m',
    status: 'upcoming',
    service_type: 'Review Call',
  },
  {
    id: 'booking-4',
    title: 'Technical Consultation',
    client_name: 'David Miller',
    client_email: 'david@cloudscale.net',
    date: 'Sep 12',
    time: '4:00 PM',
    duration: '30m',
    status: 'upcoming',
    service_type: 'Consultation',
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionHeader: React.FC<{
  title: string;
  count: number;
  linkTo: string;
  onNavigate: (path: string) => void;
  isGlobalDarkMode: boolean;
}> = ({ title, count, linkTo, onNavigate, isGlobalDarkMode }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <h2 className={cn('text-sm font-semibold', isGlobalDarkMode ? 'text-foreground' : 'text-gray-800')}>{title}</h2>
      <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', isGlobalDarkMode ? 'bg-muted text-muted-foreground' : 'bg-gray-100 text-gray-500')}>
        {count}
      </span>
    </div>
    <button
      onClick={() => onNavigate(linkTo)}
      className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
    >
      See all <ArrowRight className="w-3 h-3" />
    </button>
  </div>
);

const EmptyState: React.FC<{ label: string; isGlobalDarkMode: boolean }> = ({ label, isGlobalDarkMode }) => (
  <div className={cn('rounded-xl border border-dashed py-10 text-center', isGlobalDarkMode ? 'border-border' : 'border-gray-200')}>
    <p className="text-sm text-muted-foreground">{label}</p>
  </div>
);

const priorityColor: Record<string, string> = {
  urgent: 'text-red-500',
  high: 'text-orange-500',
  medium: 'text-yellow-500',
  low: 'text-blue-400',
};

const uptimeDot: Record<string, string> = {
  up: 'bg-emerald-500',
  down: 'bg-red-500',
  unknown: 'bg-gray-400',
};

const proposalStatusColor: Record<string, string> = {
  approved: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  pending: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  draft: 'bg-gray-500/10 text-gray-500 dark:text-gray-400',
  declined: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
  expired: 'bg-slate-500/10 text-slate-500 dark:text-slate-400',
};

const bookingStatusColor: Record<string, string> = {
  confirmed: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  upcoming: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  completed: 'bg-gray-500/10 text-gray-500 dark:text-gray-400',
  cancelled: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
};

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  percentage?: number;
  isGlobalDarkMode: boolean;
  onClick?: () => void;
}> = ({ title, value, icon, color, percentage, isGlobalDarkMode, onClick }) => {
  const isPositive = (percentage ?? 0) >= 0;
  return (
    <div
      className={cn(
        "rounded-2xl border border-border bg-card transition-all p-5 flex flex-col justify-between gap-4",
        onClick && "cursor-pointer hover:shadow-md hover:border-primary/40"
      )}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center", color)}>
          {icon}
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
      </div>
      <div>
        <div className={cn("text-xs font-medium", isGlobalDarkMode ? "text-muted-foreground" : "text-gray-500")}>
          {title}
        </div>
        <div className="text-2xl font-bold tracking-tight text-foreground mt-0.5">
          {value}
        </div>
      </div>
      {percentage !== undefined && (
        <div className="flex items-center gap-2">
          <div className={cn("flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-md",
            isPositive
              ? "text-emerald-600 bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-500"
              : "text-red-600 bg-red-100 dark:bg-red-500/10 dark:text-red-500"
          )}>
            {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {Math.abs(percentage)}%
          </div>
          <span className={cn("text-xs", isGlobalDarkMode ? "text-muted-foreground" : "text-gray-500")}>vs last period</span>
        </div>
      )}
    </div>
  );
};

const HomeSkeleton = () => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="rounded-2xl border border-border bg-card p-5 h-[140px]">
          <Skeleton className="h-9 w-9 rounded-xl mb-3" />
          <Skeleton className="h-3 w-20 mb-2" />
          <Skeleton className="h-7 w-28" />
        </div>
      ))}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-20 rounded" />
              <Skeleton className="h-4 w-6 rounded-full" />
            </div>
            <Skeleton className="h-3 w-12 rounded" />
          </div>
          <div className="space-y-4">
            {[1, 2, 3, 4].map((j) => (
              <div key={j} className="flex items-center gap-3 p-1">
                <Skeleton className="w-9 h-9 rounded-lg shrink-0" />
                <div className="flex-1 space-y-2 min-w-0">
                  <Skeleton className="h-3 w-[70%] rounded" />
                  <Skeleton className="h-2 w-[40%] rounded" />
                </div>
                <Skeleton className="h-3 w-10 rounded shrink-0" />
              </div>
            ))}
          </div>
          <Skeleton className="mt-6 h-9 w-full rounded-lg" />
        </div>
      ))}
    </div>
  </>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export const Home: React.FC<HomeProps> = ({ isGlobalDarkMode }) => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const { dispatch: brandDispatch } = useBrand();

  const firstName = profile?.first_name || 'there';

  const [tasks, setTasks] = useState<RecentTask[]>([]);
  const [brands, setBrands] = useState<RecentBrand[]>([]);
  const [sites, setSites] = useState<RecentSite[]>([]);
  const [projects, setProjects] = useState<RecentProject[]>([]);
  const [proposals, setProposals] = useState<RecentProposal[]>([]);
  const [bookings, setBookings] = useState<RecentBooking[]>([]);
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalTasks: 0,
    totalSites: 0,
    totalBrands: 0,
    pendingProposals: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentWorkspace?.id) return;

    // 1. Initial Cache Check (Synchronous) — For Instant Paint
    const cachedTasks = sessionCache.get<RecentTask[]>(`home_tasks_${currentWorkspace.id}`);
    const cachedBrands = sessionCache.get<RecentBrand[]>(`home_brands_${currentWorkspace.id}`);
    const cachedSites = sessionCache.get<RecentSite[]>(`home_sites_${currentWorkspace.id}`);
    const cachedProjects = sessionCache.get<RecentProject[]>(`home_projects_${currentWorkspace.id}`);
    const cachedProposals = sessionCache.get<RecentProposal[]>(`home_proposals_${currentWorkspace.id}`);
    const cachedBookings = sessionCache.get<RecentBooking[]>(`home_bookings_${currentWorkspace.id}`);
    const cachedStats = sessionCache.get<any>(`home_stats_${currentWorkspace.id}`);

    if (cachedTasks) setTasks(cachedTasks);
    if (cachedBrands) setBrands(cachedBrands);
    if (cachedSites) setSites(cachedSites);
    if (cachedProjects) setProjects(cachedProjects);
    if (cachedProposals) setProposals(cachedProposals);
    if (cachedBookings) setBookings(cachedBookings);
    if (cachedStats) setStats(cachedStats);

    // If we have any cached data, bypass the loading skeleton for immediate visual restore
    if (cachedTasks || cachedBrands || cachedSites || cachedProjects || cachedProposals || cachedBookings) {
      setLoading(false);
    } else {
      setLoading(true);
    }

    const fetchAll = async () => {
      await Promise.allSettled([
        // Tasks Parallel Fetch
        (async () => {
          const { data: boardsData } = await supabase
            .from('task_boards')
            .select('id, title, slug')
            .eq('workspace_id', currentWorkspace.id)
            .limit(10);

          if (boardsData && boardsData.length > 0) {
            const boardIds = boardsData.map((b: any) => b.id);
            const { data: tasksData } = await supabase
              .from('tasks')
              .select('id, title, priority, due_date, status, board_id, completed')
              .in('board_id', boardIds)
              .neq('status', 'archived')
              .eq('completed', false)
              .order('created_at', { ascending: false })
              .limit(5);

            if (tasksData) {
              const boardMap = Object.fromEntries(boardsData.map((b: any) => [b.id, { title: b.title, slug: b.slug }]));
              const mappedTasks = tasksData.map((t: any) => ({ 
                ...t, 
                board_name: boardMap[t.board_id]?.title || '',
                board_slug: boardMap[t.board_id]?.slug || ''
              }));
              setTasks(mappedTasks);
              sessionCache.set(`home_tasks_${currentWorkspace.id}`, mappedTasks);
            }
          }
        })(),

        // Brands Parallel Fetch
        (async () => {
          try {
            const brandsData = await brandService.getBrandsByWorkspace(currentWorkspace.id);
            const recentBrands = brandsData.slice(0, 5) as RecentBrand[];
            setBrands(recentBrands);
            sessionCache.set(`home_brands_${currentWorkspace.id}`, recentBrands);
          } catch (err) {
            console.error('Error fetching brands in Home:', err);
          }
        })(),

        // Sites Parallel Fetch
        (async () => {
          const { data: sitesData } = await supabase
            .from('workspace_sites')
            .select('id, name, url, uptime_status, thumbnail_url, wp_version')
            .eq('workspace_id', currentWorkspace.id)
            .order('created_at', { ascending: false })
            .limit(5);
          
          if (sitesData) {
            const recentSites = sitesData as RecentSite[];
            setSites(recentSites);
            sessionCache.set(`home_sites_${currentWorkspace.id}`, recentSites);
          }
        })(),

        // Projects Parallel Fetch
        (async () => {
          const { data: projectsData } = await supabase
            .from('projects')
            .select('id, name, status, progress, created_at')
            .eq('workspace_id', currentWorkspace.id)
            .order('created_at', { ascending: false })
            .limit(5);
          
          if (projectsData) {
            const recentProjects = projectsData as RecentProject[];
            setProjects(recentProjects);
            sessionCache.set(`home_projects_${currentWorkspace.id}`, recentProjects);
          }
        })(),

        // Proposals Parallel Fetch
        (async () => {
          try {
            const { data: proposalsData, error } = await supabase
              .from('proposals')
              .select('id, title, client_name, company_name, status, valid_until, created_at')
              .eq('workspace_id', currentWorkspace.id)
              .order('created_at', { ascending: false })
              .limit(5);

            if (!error && proposalsData && proposalsData.length > 0) {
              setProposals(proposalsData);
              sessionCache.set(`home_proposals_${currentWorkspace.id}`, proposalsData);
            } else {
              setProposals(sampleProposals);
              sessionCache.set(`home_proposals_${currentWorkspace.id}`, sampleProposals);
            }
          } catch (err) {
            console.error('Error fetching proposals in Home:', err);
            setProposals(sampleProposals);
            sessionCache.set(`home_proposals_${currentWorkspace.id}`, sampleProposals);
          }
        })(),

        // Bookings Parallel Fetch
        (async () => {
          try {
            const localBookings = safeLocalStorage.getItem(`workspace_bookings_${currentWorkspace.id}`);
            if (localBookings) {
              try {
                const parsed = JSON.parse(localBookings);
                if (Array.isArray(parsed) && parsed.length > 0) {
                  setBookings(parsed);
                  sessionCache.set(`home_bookings_${currentWorkspace.id}`, parsed);
                  return;
                }
              } catch {}
            }
            setBookings(sampleBookings);
            sessionCache.set(`home_bookings_${currentWorkspace.id}`, sampleBookings);
          } catch {
            setBookings(sampleBookings);
            sessionCache.set(`home_bookings_${currentWorkspace.id}`, sampleBookings);
          }
        })(),

        // Stats Parallel Fetch
        (async () => {
          try {
            const [projectsRes, boardsRes, sitesRes, brandsData, proposalsRes] = await Promise.all([
              supabase
                .from('projects')
                .select('*', { count: 'exact', head: true })
                .eq('workspace_id', currentWorkspace.id),
              supabase
                .from('task_boards')
                .select('id')
                .eq('workspace_id', currentWorkspace.id),
              supabase
                .from('workspace_sites')
                .select('*', { count: 'exact', head: true })
                .eq('workspace_id', currentWorkspace.id),
              brandService.getBrandsByWorkspace(currentWorkspace.id),
              supabase
                .from('proposals')
                .select('*', { count: 'exact', head: true })
                .eq('workspace_id', currentWorkspace.id)
                .eq('status', 'pending'),
            ]);

            let totalTasks = 0;
            if (boardsRes.data && boardsRes.data.length > 0) {
              const boardIds = boardsRes.data.map((b: any) => b.id);
              const { count: tasksCount } = await supabase
                .from('tasks')
                .select('*', { count: 'exact', head: true })
                .in('board_id', boardIds)
                .neq('status', 'archived')
                .eq('completed', false);
              totalTasks = tasksCount || 0;
            }

            const newStats = {
              totalProjects: projectsRes.count || 0,
              totalTasks,
              totalSites: sitesRes.count || 0,
              totalBrands: brandsData?.length || 0,
              pendingProposals: proposalsRes.count || 0,
            };
            
            setStats(newStats);
            sessionCache.set(`home_stats_${currentWorkspace.id}`, newStats);
          } catch (err) {
            console.error('Error fetching stats in Home:', err);
          }
        })()
      ]);

      setLoading(false);
    };

    fetchAll();
  }, [currentWorkspace?.id]);

  const card = 'rounded-2xl border border-border bg-card transition-all p-5';

  const row = 'flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors border border-border hover:bg-muted/50';

  if (loading && !currentWorkspace) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div 
      className={cn("flex-1 min-h-screen flex flex-col", isGlobalDarkMode ? "bg-background" : "bg-white")}
      style={{
        '--background': isGlobalDarkMode ? '240 6% 10%' : '0 0% 100%',
        '--card': isGlobalDarkMode ? '240 5% 14%' : '0 0% 100%'
      } as React.CSSProperties}
    >
      {/* ── Body ───────────────────────────────────────────────────── */}
      <div className="w-full px-6 md:px-10 pt-4 pb-8">

        <motion.h1
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className={cn('text-xl md:text-2xl font-bold tracking-tight mb-6', isGlobalDarkMode ? 'text-white' : 'text-gray-900')}
        >
          Welcome, {firstName}!
        </motion.h1>

        {loading ? (
          <HomeSkeleton />
        ) : (
          <>
            {/* ── Stats Row ─────────────────────────────────────────────── */}
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.0 }}
            >
              <StatCard
                title="Total Projects"
                value={stats.totalProjects}
                icon={<Briefcase className="w-4.5 h-4.5 text-blue-600 dark:text-blue-400" />}
                color="bg-blue-100 dark:bg-blue-500/10"
                percentage={40}
                isGlobalDarkMode={isGlobalDarkMode}
                onClick={() => navigate('/boards/projects')}
              />
              <StatCard
                title="Active Tasks"
                value={stats.totalTasks}
                icon={<CheckSquare className="w-4.5 h-4.5 text-emerald-600 dark:text-emerald-400" />}
                color="bg-emerald-100 dark:bg-emerald-500/10"
                percentage={12}
                isGlobalDarkMode={isGlobalDarkMode}
                onClick={() => navigate('/task-board')}
              />
              <StatCard
                title="Connected Sites"
                value={stats.totalSites}
                icon={<Globe className="w-4.5 h-4.5 text-cyan-600 dark:text-cyan-400" />}
                color="bg-cyan-100 dark:bg-cyan-500/10"
                percentage={5}
                isGlobalDarkMode={isGlobalDarkMode}
                onClick={() => navigate('/sites')}
              />
              <StatCard
                title="Active Brands"
                value={stats.totalBrands}
                icon={<Crown className="w-4.5 h-4.5 text-purple-600 dark:text-purple-400" />}
                color="bg-purple-100 dark:bg-purple-500/10"
                percentage={8}
                isGlobalDarkMode={isGlobalDarkMode}
                onClick={() => navigate('/brand')}
              />
              <StatCard
                title="Pending Proposals"
                value={stats.pendingProposals}
                icon={<FileText className="w-4.5 h-4.5 text-amber-600 dark:text-amber-400" />}
                color="bg-amber-100 dark:bg-amber-500/10"
                percentage={15}
                isGlobalDarkMode={isGlobalDarkMode}
                onClick={() => navigate('/proposals')}
              />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

            {/* ── Projects ───────────────────────────────────────────── */}
            <motion.div
              className={card}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
            >
              <SectionHeader
                title="Projects"
                count={projects.length}
                linkTo="/boards/projects"
                onNavigate={navigate}
                isGlobalDarkMode={isGlobalDarkMode}
              />

              {projects.length === 0 ? (
                <EmptyState label="No projects yet — create your first project." isGlobalDarkMode={isGlobalDarkMode} />
              ) : (
                <div className="space-y-1">
                  {projects.map((project) => (
                    <div key={project.id} className={row} onClick={() => navigate(`/projects/${project.id}`)}>
                      <div className={cn('w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center border', isGlobalDarkMode ? 'bg-muted border-border text-primary' : 'bg-gray-50 border-gray-100 text-primary')}>
                        <Briefcase className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={cn('text-sm font-medium truncate', isGlobalDarkMode ? 'text-foreground' : 'text-gray-800')}>
                          {project.name}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden max-w-[60px]">
                            <div 
                              className="h-full bg-primary" 
                              style={{ width: `${project.progress || 0}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-muted-foreground">{project.progress || 0}%</span>
                        </div>
                      </div>
                      <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-medium',
                        project.status === 'active' ? 'bg-emerald-500/10 text-emerald-600' :
                        project.status === 'on_hold' ? 'bg-amber-500/10 text-amber-600' :
                        'bg-blue-500/10 text-blue-600'
                      )}>
                        {project.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={() => navigate('/boards/projects')}
                className={cn('mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm border border-dashed transition-colors',
                  isGlobalDarkMode ? 'border-border text-muted-foreground hover:bg-muted hover:text-foreground' : 'border-gray-200 text-gray-400 hover:bg-gray-50 hover:text-gray-700'
                )}
              >
                <Briefcase className="w-4 h-4" />
                Go to Projects
              </button>
            </motion.div>

            {/* ── Tasks ──────────────────────────────────────────────── */}
            <motion.div
              className={card}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <SectionHeader
                title="My Tasks"
                count={tasks.length}
                linkTo="/boards"
                onNavigate={navigate}
                isGlobalDarkMode={isGlobalDarkMode}
              />

              {tasks.length === 0 ? (
                <EmptyState label="No open tasks — you're all caught up!" isGlobalDarkMode={isGlobalDarkMode} />
              ) : (
                <div className="space-y-1">
                  {tasks.map((task: any) => (
                    <div key={task.id} className={row} onClick={() => navigate(`/task-board/${task.board_slug}?task=${task.id}`)}>
                      <div className="flex-shrink-0">
                        {task.status === 'done' ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <Circle className={cn('w-4 h-4', priorityColor[task.priority] || 'text-muted-foreground')} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={cn('text-sm font-medium truncate', isGlobalDarkMode ? 'text-foreground' : 'text-gray-800')}>
                          {task.title}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {task.board_name && (
                            <span className="text-xs text-muted-foreground truncate">{task.board_name}</span>
                          )}
                          {task.due_date && (
                            <>
                              <span className="text-muted-foreground/40">·</span>
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock className="w-3 h-3" />
                                {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <span className={cn('text-[10px] font-medium capitalize px-1.5 py-0.5 rounded', priorityColor[task.priority] || 'text-muted-foreground',
                        task.priority === 'urgent' ? 'bg-red-500/10' :
                        task.priority === 'high' ? 'bg-orange-500/10' :
                        task.priority === 'medium' ? 'bg-yellow-500/10' : 'bg-blue-500/10'
                      )}>
                        {task.priority}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={() => navigate('/boards')}
                className={cn('mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm border border-dashed transition-colors',
                  isGlobalDarkMode ? 'border-border text-muted-foreground hover:bg-muted hover:text-foreground' : 'border-gray-200 text-gray-400 hover:bg-gray-50 hover:text-gray-700'
                )}
              >
                <CheckSquare className="w-4 h-4" />
                Go to Tasks
              </button>
            </motion.div>

            {/* ── Sites ──────────────────────────────────────────────── */}
            <motion.div
              className={card}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <SectionHeader
                title="Sites"
                count={sites.length}
                linkTo="/sites"
                onNavigate={navigate}
                isGlobalDarkMode={isGlobalDarkMode}
              />

              {sites.length === 0 ? (
                <EmptyState label="No sites connected — add your first WordPress site." isGlobalDarkMode={isGlobalDarkMode} />
              ) : (
                <div className="space-y-1">
                  {sites.map((site) => {
                    const domain = (() => {
                      try { return new URL(site.url).hostname; } catch { return site.url; }
                    })();
                    const status = site.uptime_status || 'unknown';
                    return (
                      <div key={site.id} className={row} onClick={() => navigate(`/sites/${site.id}`)}>
                        {/* Favicon — high-fidelity favicon using Google S2 */}
                        <div className={cn('w-9 h-9 rounded-lg flex-shrink-0 overflow-hidden flex items-center justify-center border', isGlobalDarkMode ? 'bg-muted border-border' : 'bg-gray-50 border-gray-100')}>
                          <img 
                            src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`} 
                            alt={site.name} 
                            className="w-5 h-5 object-contain"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).parentElement!.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-globe w-4 h-4 text-muted-foreground"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><path d="M2 12h20"/></svg>';
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={cn('text-sm font-medium truncate', isGlobalDarkMode ? 'text-foreground' : 'text-gray-800')}>
                            {site.name}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">{domain}</p>
                        </div>
                        {/* Uptime status */}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span className={cn('w-2 h-2 rounded-full', uptimeDot[status] || uptimeDot.unknown)} />
                          <span className="text-xs capitalize text-muted-foreground">{status}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                onClick={() => navigate('/sites')}
                className={cn('mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm border border-dashed transition-colors',
                  isGlobalDarkMode ? 'border-border text-muted-foreground hover:bg-muted hover:text-foreground' : 'border-gray-200 text-gray-400 hover:bg-gray-50 hover:text-gray-700'
                )}
              >
                <Globe className="w-4 h-4" />
                Go to Sites
              </button>
            </motion.div>

            {/* ── Brands ─────────────────────────────────────────────── */}
            <motion.div
              className={card}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <SectionHeader
                title="Brands"
                count={brands.length}
                linkTo="/brand"
                onNavigate={navigate}
                isGlobalDarkMode={isGlobalDarkMode}
              />

              {brands.length === 0 ? (
                <EmptyState label="No brands yet — create your first brand." isGlobalDarkMode={isGlobalDarkMode} />
              ) : (
                <div className="space-y-1">
                  {brands.map((brand) => {
                    const initials = brand.name.slice(0, 2).toUpperCase();
                    const primaryColor = brand.colors?.primary || '#6366f1';
                    return (
                      <div 
                        key={brand.id} 
                        className={row} 
                        onClick={() => {
                          brandDispatch({ type: 'SET_CURRENT_BRAND_ID', payload: brand.id });
                          navigate(`/preview?brandId=${brand.id}`);
                        }}
                      >
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {brand.logo_url ? (
                            <img src={brand.logo_url} alt={brand.name} className="w-full h-full object-contain" />
                          ) : initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={cn('text-sm font-medium truncate', isGlobalDarkMode ? 'text-foreground' : 'text-gray-800')}>
                            {brand.name}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">{brand.industry || 'No industry set'}</p>
                        </div>
                        <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-medium',
                          brand.status === 'live' ? 'bg-emerald-500/10 text-emerald-600' :
                          brand.status === 'draft' ? 'bg-yellow-500/10 text-yellow-600' :
                          'bg-gray-500/10 text-gray-500'
                        )}>
                          {brand.status}
                        </span>
                        {/* Color swatches */}
                        <div className="flex gap-0.5 flex-shrink-0">
                          {[brand.colors?.primary, brand.colors?.secondary, brand.colors?.accent].filter(Boolean).map((c, i) => (
                            <div key={i} className="w-3 h-3 rounded-full border border-white/20" style={{ backgroundColor: c }} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                onClick={() => navigate('/brand')}
                className={cn('mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm border border-dashed transition-colors',
                  isGlobalDarkMode ? 'border-border text-muted-foreground hover:bg-muted hover:text-foreground' : 'border-gray-200 text-gray-400 hover:bg-gray-50 hover:text-gray-700'
                )}
              >
                <Layers className="w-4 h-4" />
                Go to Brands
              </button>
            </motion.div>

            {/* ── Proposals ───────────────────────────────────────────── */}
            <motion.div
              className={card}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <SectionHeader
                title="Proposals"
                count={proposals.length}
                linkTo="/proposals"
                onNavigate={navigate}
                isGlobalDarkMode={isGlobalDarkMode}
              />

              {proposals.length === 0 ? (
                <EmptyState label="No proposals yet — create your first proposal." isGlobalDarkMode={isGlobalDarkMode} />
              ) : (
                <div className="space-y-1">
                  {proposals.map((proposal) => {
                    const status = proposal.status || 'draft';
                    const client = proposal.client_name || proposal.company_name || 'Client';
                    return (
                      <div
                        key={proposal.id}
                        className={row}
                        onClick={() => {
                          if (proposal.id.startsWith('sample-')) {
                            navigate('/proposals');
                          } else {
                            navigate(`/proposals/${proposal.id}/edit`);
                          }
                        }}
                      >
                        <div className={cn('w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center border', isGlobalDarkMode ? 'bg-amber-500/10 border-amber-500/20 text-amber-400' : 'bg-amber-50 border-amber-200 text-amber-600')}>
                          <FileText className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={cn('text-sm font-medium truncate', isGlobalDarkMode ? 'text-foreground' : 'text-gray-800')}>
                            {proposal.title || 'Untitled Proposal'}
                          </p>
                          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground truncate">
                            <span className="truncate">{client}</span>
                            {proposal.valid_until && (
                              <>
                                <span className="text-muted-foreground/40">·</span>
                                <span className="flex items-center gap-1 shrink-0">
                                  <Clock className="w-3 h-3" />
                                  Due {new Date(proposal.valid_until).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-medium capitalize shrink-0',
                          proposalStatusColor[status] || 'bg-gray-500/10 text-gray-500'
                        )}>
                          {status}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                onClick={() => navigate('/proposals')}
                className={cn('mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm border border-dashed transition-colors',
                  isGlobalDarkMode ? 'border-border text-muted-foreground hover:bg-muted hover:text-foreground' : 'border-gray-200 text-gray-400 hover:bg-gray-50 hover:text-gray-700'
                )}
              >
                <FileText className="w-4 h-4" />
                Go to Proposals
              </button>
            </motion.div>

            {/* ── Bookings ────────────────────────────────────────────── */}
            <motion.div
              className={card}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <SectionHeader
                title="Bookings"
                count={bookings.length}
                linkTo="/calendar"
                onNavigate={navigate}
                isGlobalDarkMode={isGlobalDarkMode}
              />

              {bookings.length === 0 ? (
                <EmptyState label="No upcoming bookings — your calendar is clear." isGlobalDarkMode={isGlobalDarkMode} />
              ) : (
                <div className="space-y-1">
                  {bookings.map((booking) => {
                    const status = booking.status || 'confirmed';
                    return (
                      <div
                        key={booking.id}
                        className={row}
                        onClick={() => navigate('/calendar')}
                      >
                        <div className={cn('w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center border', isGlobalDarkMode ? 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400' : 'bg-indigo-50 border-indigo-200 text-indigo-600')}>
                          <CalendarIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={cn('text-sm font-medium truncate', isGlobalDarkMode ? 'text-foreground' : 'text-gray-800')}>
                            {booking.title}
                          </p>
                          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground truncate">
                            <span className="truncate">{booking.client_name}</span>
                            <span className="text-muted-foreground/40">·</span>
                            <span className="flex items-center gap-1 shrink-0">
                              <Clock className="w-3 h-3" />
                              {booking.date}, {booking.time}
                            </span>
                          </div>
                        </div>
                        <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-medium capitalize shrink-0',
                          bookingStatusColor[status] || 'bg-blue-500/10 text-blue-600'
                        )}>
                          {status}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                onClick={() => navigate('/calendar')}
                className={cn('mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm border border-dashed transition-colors',
                  isGlobalDarkMode ? 'border-border text-muted-foreground hover:bg-muted hover:text-foreground' : 'border-gray-200 text-gray-400 hover:bg-gray-50 hover:text-gray-700'
                )}
              >
                <CalendarIcon className="w-4 h-4" />
                Go to Calendar
              </button>
            </motion.div>
          </div>
          </>
        )}

      </div>
    </div>
  );
};
